import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@contexts/auth';
import { BaseService } from '@utility/services/base';
import { APIPath } from '@utility/constants/api';

/* EN-4396: one account of the sidebar switcher. `accountLevel` is the
   server's answer to "may this user pick the account level here"; the
   divisions are the account's (orgs, by id), of which the user may hold only
   some. */
export type SwitcherAccount = {
  id: string;
  name: string;
  accountLevel: boolean;
  /** The account level is switched on for the account (whether or not this
      user may pick it): a user without it is told to ask an admin. */
  accountViewEnabled?: boolean;
  /** The account's workspace icon; none = the building icon. */
  logoUrl?: string | null;
  divisionIds: string[];
  /** Per-division icons from Postgres; optional for older API responses. */
  divisions?: { id: string; logoUrl: string | null }[];
};

const wellFormed = (a: Partial<SwitcherAccount> | null): a is SwitcherAccount =>
  !!a &&
  typeof a.id === 'string' &&
  typeof a.name === 'string' &&
  typeof a.accountLevel === 'boolean' &&
  Array.isArray(a.divisionIds) &&
  a.divisionIds.every(id => typeof id === 'string');

/* The accounts the switcher groups divisions under. An error, an empty
   answer or anything malformed is no accounts at all: the flat list. */
export function useAccountSwitcher(): SwitcherAccount[] {
  const { user } = useAuth();
  const userId = user?._id;
  const query = useQuery<{ accounts?: Partial<SwitcherAccount>[] }>({
    queryKey: ['account-switcher', userId],
    queryFn: () => BaseService.get(APIPath.accountsSwitcher),
    enabled: !!userId,
    retry: false,
    staleTime: 60_000,
  });
  const accounts = query.data?.accounts;
  if (!Array.isArray(accounts)) return [];
  /* The optional fields are kept only when they are what they say. */
  return accounts.filter(wellFormed).map(a => ({
    ...a,
    accountViewEnabled: a.accountViewEnabled === true,
    logoUrl: typeof a.logoUrl === 'string' && a.logoUrl ? a.logoUrl : null,
    ...(a.divisions === undefined
      ? {}
      : {
          divisions: Array.isArray(a.divisions)
            ? a.divisions
                .filter(
                  d =>
                    d &&
                    typeof d.id === 'string' &&
                    a.divisionIds.includes(d.id),
                )
                .map(d => ({
                  id: d.id,
                  logoUrl:
                    typeof d.logoUrl === 'string' && d.logoUrl
                      ? d.logoUrl
                      : null,
                }))
            : [],
        }),
  }));
}
